import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface Testimonial {
  jobTitle: string
  dateRange: string
  rating: number
  testimonial: string
  author: string
  position: string
  image?: string
}

const Testimonials: React.FC = () => {
  const { t } = useLanguage()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)

  const testimonials: Testimonial[] = [
    {
      jobTitle: t('testimonials.items.0.jobTitle'),
      dateRange: t('testimonials.items.0.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.0.testimonial'),
      author: t('testimonials.items.0.author'),
      position: t('testimonials.items.0.position')
    },
    {
      jobTitle: t('testimonials.items.1.jobTitle'),
      dateRange: t('testimonials.items.1.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.1.testimonial'),
      author: t('testimonials.items.1.author'),
      position: t('testimonials.items.1.position')
    },
    {
      jobTitle: t('testimonials.items.2.jobTitle'),
      dateRange: t('testimonials.items.2.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.2.testimonial'),
      author: t('testimonials.items.2.author'),
      position: t('testimonials.items.2.position')
    },
    {
      jobTitle: t('testimonials.items.3.jobTitle'),
      dateRange: t('testimonials.items.3.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.3.testimonial'),
      author: t('testimonials.items.3.author'),
      position: t('testimonials.items.3.position')
    },
    {
      jobTitle: t('testimonials.items.4.jobTitle'),
      dateRange: t('testimonials.items.4.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.4.testimonial'),
      author: t('testimonials.items.4.author'),
      position: t('testimonials.items.4.position')
    },
    {
      jobTitle: t('testimonials.items.5.jobTitle'),
      dateRange: t('testimonials.items.5.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.5.testimonial'),
      author: t('testimonials.items.5.author'),
      position: t('testimonials.items.5.position')
    },
    {
      jobTitle: t('testimonials.items.6.jobTitle'),
      dateRange: t('testimonials.items.6.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.6.testimonial'),
      author: t('testimonials.items.6.author'),
      position: t('testimonials.items.6.position')
    },
    {
      jobTitle: t('testimonials.items.7.jobTitle'),
      dateRange: t('testimonials.items.7.dateRange'),
      rating: 5.0,
      testimonial: t('testimonials.items.7.testimonial'),
      author: t('testimonials.items.7.author'),
      position: t('testimonials.items.7.position')
    },
    {
      jobTitle: t('testimonials.items.8.jobTitle'),
      dateRange: t('testimonials.items.8.dateRange'),
      rating: 4.6,
      testimonial: t('testimonials.items.8.testimonial'),
      author: t('testimonials.items.8.author'),
      position: t('testimonials.items.8.position')
    }
  ]

  // Auto-rotate carousel (pauses on hover)
  useEffect(() => {
    if (isHovered) return // Don't auto-rotate when hovered

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length)
    }, 8000) // Change every 8 seconds (6 seconds visible + 2 seconds for transition)

    return () => clearInterval(interval)
  }, [testimonials.length, isHovered])

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    )
  }

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length)
  }

  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating)
    const hasHalfStar = rating % 1 !== 0
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0)

    return (
      <div className="flex items-center space-x-1">
        {[...Array(fullStars)].map((_, i) => (
          <Star key={`full-${i}`} className="h-5 w-5 text-yellow-400 fill-current" />
        ))}
        {hasHalfStar && (
          <div className="relative">
            <Star className="h-5 w-5 text-gray-300 fill-current" />
            <div className="absolute inset-0 overflow-hidden" style={{ width: '50%' }}>
              <Star className="h-5 w-5 text-yellow-400 fill-current" />
            </div>
          </div>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <Star key={`empty-${i}`} className="h-5 w-5 text-gray-300 fill-current" />
        ))}
        <span className="ml-2 text-sm text-gray-600 font-medium">{rating.toFixed(1)}</span>
      </div>
    )
  }

  const currentTestimonial = testimonials[currentIndex]

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" id="testimonials">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            {t('testimonials.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {t('testimonials.description')}
          </p>
        </motion.div>

        <div className="relative max-w-5xl mx-auto">
          {/* Carousel Container */}
          <div 
            className="relative overflow-hidden rounded-3xl bg-white shadow-2xl"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.8, ease: 'easeInOut' }}
                className="p-8 md:p-12"
              >
                {/* Quote Icon */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                    <Quote className="h-8 w-8 text-white" />
                  </div>
                </div>

                {/* Job Title and Date */}
                <div className="text-center mb-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">
                    {currentTestimonial.jobTitle}
                  </h3>
                  <p className="text-sm text-gray-500">{currentTestimonial.dateRange}</p>
                </div>

                {/* Rating */}
                <div className="flex justify-center mb-6">
                  {renderStars(currentTestimonial.rating)}
                </div>

                {/* Testimonial Text */}
                <blockquote className="text-xl md:text-2xl font-medium text-gray-900 mb-8 leading-relaxed text-center px-4">
                  "{currentTestimonial.testimonial}"
                </blockquote>

                {/* Author */}
                <div className="flex items-center justify-center space-x-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
                    {currentTestimonial.author.charAt(0).toUpperCase()}
                  </div>
                  <div className="text-left">
                    <h4 className="text-lg font-semibold text-gray-900">{currentTestimonial.author}</h4>
                    <p className="text-gray-600">{currentTestimonial.position}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
              aria-label="Previous testimonial"
            >
              <ChevronLeft className="h-6 w-6 text-gray-700" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
              aria-label="Next testimonial"
            >
              <ChevronRight className="h-6 w-6 text-gray-700" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center space-x-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'bg-blue-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Testimonials
